import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";

const SUBMIT_URL = "https://readdy.ai/api/form/daikp8rp14a1h3mr75r0";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const topics = ["爆料线索提交", "频道补充建议", "内容纠错", "合作与投稿", "其他反馈"];

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系我们 - 看料社",
        url: "https://sdyjh.cn/contact",
        inLanguage: "zh-CN",
        description: "通过留言表单或邮件联系看料社，提交爆料线索、频道补充建议或内容纠错。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://sdyjh.cn/" },
          { "@type": "ListItem", position: 2, name: "联系我们", item: "https://sdyjh.cn/contact" },
        ],
      },
    ],
    [],
  );

  useSeo({
    title: "联系我们 - 线索提交与内容反馈 | 看料社",
    description:
      "联系看料社：提交公开爆料线索、频道补充建议或内容纠错。可通过站内留言表单或页面邮箱联系我们，编辑会逐条查看反馈。",
    keywords: "联系看料社,爆料线索提交,频道补充建议,内容纠错",
    canonicalPath: "/contact",
    ogType: "website",
    jsonLd,
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);
    const honey = String(formData.get("website_alt") || "").trim();

    if (honey) {
      setStatus("success");
      setFormError("");
      formEl.reset();
      setMessageLength(0);
      return;
    }

    formData.delete("website_alt");
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, typeof value === "string" ? value : "");
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交未成功，请稍后重试，或直接发送邮件联系我们。",
        );
        return;
      }

      setStatus("success");
      formEl.reset();
      setMessageLength(0);
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-6 pt-28 md:pt-36 pb-10 md:pb-14">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
            <h1 className="mt-5 font-heading font-black text-3xl md:text-5xl text-foreground-950 leading-tight">
              联系我们
            </h1>
            <p className="mt-4 text-sm md:text-lg text-foreground-600 max-w-3xl leading-relaxed">
              有公开线索、频道补充建议或内容纠错，都可以通过下面的表单留言。我们会在内容整理时段内逐条查看，但无法保证每条都能回复或成稿。
            </p>
          </div>
        </section>

        <section className="w-full">
          <div className="w-full px-4 md:px-6 py-12 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
              <div className="lg:col-span-5">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">其他联系方式</h2>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  如果不方便使用表单，也可以直接发邮件。为了让编辑更快判断，建议在邮件里写清楚信息方向与来源类型。
                </p>

                <div className="mt-6 flex flex-col gap-4">
                  <div className="flex items-start gap-4 p-5 rounded-lg bg-background-100 border border-background-200">
                    <span className="w-11 h-11 shrink-0 rounded-md flex items-center justify-center text-xl bg-primary-100 text-primary-600">
                      <i className="ri-mail-line" />
                    </span>
                    <div>
                      <h3 className="font-heading font-semibold text-base text-foreground-950">电子邮箱</h3>
                      <p className="mt-1 text-sm text-foreground-600">hi@kanliaoshe.example.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-5 rounded-lg bg-background-100 border border-background-200">
                    <span className="w-11 h-11 shrink-0 rounded-md flex items-center justify-center text-xl bg-accent-100 text-accent-800">
                      <i className="ri-time-line" />
                    </span>
                    <div>
                      <h3 className="font-heading font-semibold text-base text-foreground-950">内容整理时段</h3>
                      <p className="mt-1 text-sm text-foreground-600">每日 09:00 - 23:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-5 rounded-lg bg-background-100 border border-background-200">
                    <span className="w-11 h-11 shrink-0 rounded-md flex items-center justify-center text-xl bg-secondary-100 text-secondary-800">
                      <i className="ri-shield-check-line" />
                    </span>
                    <div>
                      <h3 className="font-heading font-semibold text-base text-foreground-950">线索建议</h3>
                      <p className="mt-1 text-sm text-foreground-600 leading-relaxed">
                        请只提交公开渠道可见的信息，并附上大致来源；我们不会处理涉及私人信息的内容。
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200">
                  <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">站内留言</h2>
                  <p className="mt-2 text-sm text-foreground-600">带 * 的为必填项，留言内容请在 500 字以内。</p>

                  {status === "success" && (
                    <div className="mt-6 flex items-start gap-3 p-4 rounded-md bg-accent-100 border border-accent-300">
                      <span className="w-6 h-6 flex items-center justify-center text-accent-800 shrink-0">
                        <i className="ri-checkbox-circle-line text-lg" />
                      </span>
                      <p className="text-sm text-foreground-800 leading-relaxed">
                        留言已提交，感谢你的反馈。编辑会在整理时段内查看，如需要进一步核实我们会通过邮件联系你。
                      </p>
                    </div>
                  )}

                  {status === "error" && (
                    <div className="mt-6 flex items-start gap-3 p-4 rounded-md bg-secondary-100 border border-secondary-300">
                      <span className="w-6 h-6 flex items-center justify-center text-secondary-800 shrink-0">
                        <i className="ri-error-warning-line text-lg" />
                      </span>
                      <p className="text-sm text-foreground-800 leading-relaxed">{formError}</p>
                    </div>
                  )}

                  <form
                    id="contact-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="mt-6 flex flex-col gap-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-2">
                        <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
                          称呼 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          maxLength={40}
                          placeholder="怎么称呼你"
                          className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          placeholder="用于回复联系"
                          className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label htmlFor="contact-topic" className="text-sm font-medium text-foreground-800">
                        反馈类型 <span className="text-primary-500">*</span>
                      </label>
                      <select
                        id="contact-topic"
                        name="topic"
                        required
                        defaultValue=""
                        className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors cursor-pointer"
                      >
                        <option value="" disabled>请选择反馈类型</option>
                        {topics.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={6}
                        onChange={(e) => setMessageLength(e.target.value.length)}
                        placeholder="请说明线索方向或反馈内容，并尽量附上来源类型"
                        className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                      />
                      <span className="self-end text-xs text-foreground-500">{messageLength} / 500</span>
                    </div>

                    <input
                      type="text"
                      name="website_alt"
                      className="form-guard-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md text-sm font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" />
                          提交中...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line" />
                          提交留言
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}